import { useEffect, useState, useCallback } from "react";
import api, { apiError, money, resolveImg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { ImageUploader } from "@/components/ImageUploader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Package, Loader2, Check, ChevronLeft, ChevronRight } from "lucide-react";

const EMPTY = { name: "", stock_code: "", brand: "", stock: 0, price: 0, cost_price: 0, currency: "TRY", image_url: "", description: "" };
const PAGE_SIZE = 50;

export default function Products() {
  const { can } = useAuth();
  const canCreate = can("product_create");
  const canEdit = can("product_edit");
  const canDelete = can("product_delete");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [stockEdit, setStockEdit] = useState({});
  const [page, setPage] = useState(0);
  const [brand, setBrand] = useState("all");
  const [brands, setBrands] = useState([]);

  const load = useCallback(async (query = "", brandVal = "all", pageNum = 0) => {
    setLoading(true);
    try {
      const res = await api.get("/products", { params: { q: query, brand: brandVal === "all" ? "" : brandVal, limit: PAGE_SIZE, skip: pageNum * PAGE_SIZE } });
      setItems(res.data);
      setTotal(Number(res.headers["x-total-count"]) || 0);
    } finally { setLoading(false); }
  }, []);

  const loadBrands = useCallback(() => { api.get("/products/brands").then((r) => setBrands(r.data)).catch(() => {}); }, []);

  useEffect(() => { loadBrands(); }, [loadBrands]);
  useEffect(() => { const t = setTimeout(() => { setPage(0); load(q, brand, 0); }, 300); return () => clearTimeout(t); }, [q, brand, load]);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (p) => { setForm({ ...EMPTY, ...p }); setEditId(p.id); setOpen(true); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.name) { toast.error("Ürün ismi zorunludur"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name, stock_code: form.stock_code, brand: form.brand, stock: Number(form.stock) || 0,
        price: Number(form.price) || 0, cost_price: Number(form.cost_price) || 0, currency: form.currency, image_url: form.image_url, description: form.description,
      };
      if (editId) await api.put(`/products/${editId}`, payload);
      else await api.post("/products", payload);
      toast.success(editId ? "Ürün güncellendi" : "Ürün eklendi");
      setOpen(false); load(q, brand, page);
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/products/${id}`); toast.success("Ürün silindi"); load(q, brand, page); }
    catch (err) { toast.error(apiError(err)); }
  };

  const saveStock = async (id) => {
    try {
      await api.patch(`/products/${id}/stock`, { stock: Number(stockEdit[id]) });
      toast.success("Stok güncellendi");
      setStockEdit((s) => { const n = { ...s }; delete n[id]; return n; });
      load(q, brand, page);
    } catch (err) { toast.error(apiError(err)); }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pageItems = items;
  const goPage = (p) => { setPage(p); load(q, brand, p); };

  return (
    <div>
      <PageHeader
        title="Ürünler"
        subtitle={`Ürün kataloğunuz ve stoklarınız · Toplam ${total} ürün`}
        action={canCreate ? (
          <Button className="bg-rose-600 hover:bg-rose-700 text-white rounded-xl" onClick={openNew} data-testid="add-product-btn">
            <Plus className="w-4 h-4 mr-2" /> Yeni Ürün
          </Button>
        ) : null}
      />

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Ürün adı, stok kodu veya marka ara..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="product-search" />
        </div>
        <Select value={brand} onValueChange={setBrand}>
          <SelectTrigger className="w-full sm:w-56" data-testid="product-brand-filter"><SelectValue placeholder="Marka" /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">Tüm Markalar</SelectItem>
            {brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-rose-600" /></div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center border-slate-100 rounded-2xl card-soft">
          <Package className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">Ürün bulunamadı.</p>
        </Card>
      ) : (
        <>
          <Card className="border-slate-100 rounded-2xl card-soft overflow-hidden divide-y divide-slate-100">
            {pageItems.map((p) => (
              <div key={p.id} className="flex items-center gap-3 p-3 sm:p-4 hover:bg-slate-50 transition-colors" data-testid={`product-card-${p.id}`}>
                <div className="w-14 h-14 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
                  {p.image_url ? <img src={resolveImg(p.image_url)} alt={p.name} className="w-full h-full object-cover" /> : <Package className="w-6 h-6 text-slate-300" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-slate-900 truncate">{p.name}</p>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    {p.stock_code && <Badge variant="outline" className="text-xs font-normal">{p.stock_code}</Badge>}
                    {p.brand && <span className="text-xs text-slate-500">{p.brand}</span>}
                  </div>
                </div>
                <div className="hidden md:block text-right w-28 shrink-0">
                  <p className="font-heading font-bold text-sm">{money(p.price, p.currency)}</p>
                </div>
                <div className="shrink-0 flex items-center justify-end gap-2">
                  <span className="text-xs text-slate-400 hidden sm:inline">Stok:</span>
                  {canEdit && stockEdit[p.id] !== undefined ? (
                    <div className="flex items-center gap-1">
                      <Input type="number" value={stockEdit[p.id]} onChange={(e) => setStockEdit((s) => ({ ...s, [p.id]: e.target.value }))} className="h-8 w-16 text-sm" data-testid={`stock-input-${p.id}`} />
                      <Button size="icon" className="h-8 w-8 bg-rose-600 hover:bg-rose-700" onClick={() => saveStock(p.id)} data-testid={`stock-save-${p.id}`}><Check className="w-4 h-4" /></Button>
                    </div>
                  ) : canEdit ? (
                    <button className="text-sm font-semibold hover:text-rose-600 underline decoration-dotted" onClick={() => setStockEdit((s) => ({ ...s, [p.id]: p.stock }))} data-testid={`stock-edit-${p.id}`}>{p.stock} adet</button>
                  ) : (
                    <span className="text-sm font-semibold">{p.stock} adet</span>
                  )}
                </div>
                {(canEdit || canDelete) && (
                  <div className="flex gap-1 shrink-0">
                    {canEdit && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`}><Pencil className="w-4 h-4" /></Button>}
                    {canDelete && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600" data-testid={`delete-product-${p.id}`}><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Ürünü sil?</AlertDialogTitle><AlertDialogDescription>Bu işlem geri alınamaz.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Vazgeç</AlertDialogCancel><AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={() => remove(p.id)}>Sil</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                )}
              </div>
            ))}
          </Card>
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-slate-500">Sayfa {page + 1}/{totalPages} · {total} ürün</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="rounded-xl" disabled={page === 0} onClick={() => goPage(Math.max(0, page - 1))} data-testid="product-prev"><ChevronLeft className="w-4 h-4 mr-1" />Önceki</Button>
              <Button variant="outline" size="sm" className="rounded-xl" disabled={page + 1 >= totalPages} onClick={() => goPage(page + 1)} data-testid="product-next">Sonraki<ChevronRight className="w-4 h-4 ml-1" /></Button>
            </div>
          </div>
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Ürünü Düzenle" : "Yeni Ürün"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Ürün Görseli</Label><ImageUploader value={form.image_url} onChange={(url) => setForm((f) => ({ ...f, image_url: url }))} /></div>
            <div className="space-y-2"><Label>Ürün İsmi *</Label><Input value={form.name} onChange={set("name")} data-testid="pf-name" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Stok Kodu</Label><Input value={form.stock_code} onChange={set("stock_code")} data-testid="pf-code" /></div>
              <div className="space-y-2"><Label>Marka</Label><Input value={form.brand} onChange={set("brand")} data-testid="pf-brand" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Stok</Label><Input type="number" value={form.stock} onChange={set("stock")} data-testid="pf-stock" /></div>
              <div className="space-y-2"><Label>Para Birimi</Label>
                <Select value={form.currency} onValueChange={(v) => setForm((f) => ({ ...f, currency: v }))}>
                  <SelectTrigger data-testid="pf-currency"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="TRY">₺ TL</SelectItem><SelectItem value="EUR">€ EUR</SelectItem><SelectItem value="USD">$ USD</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Alış Fiyatı</Label><Input type="number" value={form.cost_price} onChange={set("cost_price")} data-testid="pf-cost" /><p className="text-[11px] text-slate-400">Tekliflerde uyarı eşiği olarak kullanılır.</p></div>
              <div className="space-y-2"><Label>Satış Fiyatı</Label><Input type="number" value={form.price} onChange={set("price")} data-testid="pf-price" /></div>
            </div>
            <div className="space-y-2"><Label>Açıklama</Label><Textarea value={form.description} onChange={set("description")} rows={3} data-testid="pf-desc" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={save} disabled={saving} data-testid="save-product-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kaydet"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
